import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useMangoApi } from '~/atoms/MangoApi'
import Logger from '~/services/Logger'
import {
  CreateThreadResponse,
  PostMessageParams,
  SendMessageResponse,
} from '~/types/MangoApiClientTypes'
import { newUuid } from '~/utils/common'

import {
  Message,
  MessageGroup,
  MessageThread,
  PaginatedApiResult,
} from '@mangovoice/types'
import { ApiError } from '@mangovoice/types'

export interface GetMessageThreadsParams {
  offset?: number | null
  search?: string | null
  groupId?: number | null
  hasUnreadMessages?: boolean | null
}

interface UseGetMessageThreadsParams {
  pbxId?: number
  onQuerySuccess?: (data: PaginatedApiResult<MessageThread>) => void
  options?: Omit<
    UseQueryOptions<PaginatedApiResult<MessageThread>, ApiError>,
    'queryKey'
  >
}

const logger = new Logger('messagingQueriesLogger')
/** Fetches MessageThreads from MangoAPI.  Query will not run by default as it is dependent upon option data.
 ** Extract the refetch function from the returned object to run the query manually with option data.
 */
export const useGetMessageThreads = ({
  pbxId,
  onQuerySuccess,
  options = {},
}: UseGetMessageThreadsParams) => {
  const mangoApi = useMangoApi()
  const [params, setParams] = useState<GetMessageThreadsParams>()

  const refetch = (newMessageThreadParams: GetMessageThreadsParams) => {
    setParams(newMessageThreadParams)
  }

  const query = useQuery<PaginatedApiResult<MessageThread>, ApiError>({
    queryKey: [
      'messageThreads',
      params?.offset ?? 0,
      params?.search ?? 0,
      params?.groupId,
      params?.hasUnreadMessages,
    ],
    queryFn: async () => {
      const { groupId, offset, search } = params ?? {}
      const groupIdActual = groupId === 0 ? undefined : groupId

      const { ok, data } = await mangoApi.getMessageThreads({
        offset: offset ?? 0,
        search: search ?? '',
        groupId: groupIdActual ?? undefined,
        pbxId,
        hasUnreadMessages: params?.hasUnreadMessages ?? undefined,
      })

      if (!ok) return Promise.reject(data as ApiError)
      const messageThreadData = data as PaginatedApiResult<MessageThread>
      onQuerySuccess?.(messageThreadData)
      return messageThreadData
    },
    ...options,
    retry: (options.retry as number | boolean | undefined) ?? false,
  })

  useEffect(() => {
    if (!params) return
    query.refetch()
  }, [params])

  return {
    ...query,
    refetch: refetch,
  }
}

interface UseGetMessageThreadParams {
  onQuerySuccess?: (data: MessageThread) => void
  options?: Omit<UseQueryOptions<MessageThread, ApiError>, 'queryKey'>
}

/**
 * @description Always sets the query to be disabled initially - use refetch with threadId to initiate query
 * @date 8/29/2023 - 12:35:44 PM
 *
 * @param {{ onQuerySuccess: any; options: any; }} { onQuerySuccess, options }
 * @returns {*}
 */
export const useGetMessageThread = ({
  onQuerySuccess,
  options = {},
}: UseGetMessageThreadParams) => {
  const [params, setParams] = useState<{
    threadId: number
  }>({
    threadId: 0,
  })
  const mangoApi = useMangoApi()
  const query = useQuery<MessageThread, ApiError>({
    queryKey: ['messageThread', params],
    queryFn: async () => {
      if (params.threadId === 0) return Promise.resolve({} as MessageThread)

      const { ok, data } = await mangoApi.getMessageThread(params.threadId)

      if (!ok) return Promise.reject(data)

      const messageThreadData = data!
      onQuerySuccess?.(messageThreadData)
      return messageThreadData
    },
    ...options,
    enabled: false,
    retry: (options.retry as number | boolean | undefined) ?? false,
  })

  const refetch = (newThreadId: number) => {
    setParams({
      threadId: newThreadId,
    })
  }

  useEffect(() => {
    query.refetch()
  }, [params])

  return {
    ...query,
    refetch: refetch,
  }
}

interface GetThreadMessagesParams {
  offset?: number
  limit?: number
}
interface UseGetThreadMessagesParams {
  threadId: number
  onQuerySuccess?: (data: PaginatedApiResult<Message>) => void
  options?: Omit<
    UseQueryOptions<PaginatedApiResult<Message>, ApiError>,
    'enabled' | 'queryKey'
  >
}

/**
 * @description Always sets the query to be disabled initially - use refetch with query parameters to initiate query
 * @date 8/21/2023 - 2:39:06 PM
 *
 * @param {UseGetThreadMessagesParams} {
  threadId,
  onQuerySuccess,
  options,
}
 * @returns {*}
 */
export const useGetThreadMessages = ({
  threadId,
  onQuerySuccess,
  options,
}: UseGetThreadMessagesParams) => {
  const mangoApi = useMangoApi()
  const [params, setParams] = useState<GetThreadMessagesParams>()
  const refetch = ({ offset, limit }: GetThreadMessagesParams) => {
    setParams({ offset, limit })
  }

  const query = useQuery<PaginatedApiResult<Message>, ApiError>({
    queryKey: [
      'threadMessages',
      threadId,
      params?.offset ?? 0,
      params?.limit ?? 0,
    ],
    queryFn: async () => {
      if (threadId === 0)
        return Promise.resolve({} as PaginatedApiResult<Message>)

      const { ok, data: messageData } = await mangoApi.getMessages({
        threadId,
        ...params,
      })

      if (!ok) {
        return Promise.reject(messageData)
      }

      onQuerySuccess?.(messageData!)
      return messageData!
    },
    ...options,
    enabled: false,
    retry: (options?.retry as number | boolean | undefined) ?? false,
  })

  useEffect(() => {
    query.refetch()
  }, [params])

  return { ...query, refetch: refetch }
}

export interface UseGetMessageGroupsParams {
  pbxId?: number
  onQuerySuccess?: (data: PaginatedApiResult<MessageGroup>) => void
  options?: Omit<
    UseQueryOptions<PaginatedApiResult<MessageGroup>, ApiError>,
    'queryKey'
  >
}
export const useGetMessageGroups = ({
  pbxId,
  onQuerySuccess,
  options = {},
}: UseGetMessageGroupsParams) => {
  const mangoApi = useMangoApi()
  const query = useQuery<PaginatedApiResult<MessageGroup>, ApiError>({
    queryKey: ['messageGroups', pbxId],
    queryFn: async () => {
      if (pbxId === 0)
        return Promise.resolve({} as PaginatedApiResult<MessageGroup>)

      const { ok, data } = await mangoApi.getMessageGroups(pbxId)

      if (!ok) {
        return Promise.reject(data as ApiError)
      }

      const messageGroupData = data as PaginatedApiResult<MessageGroup>
      onQuerySuccess?.(messageGroupData)
      return messageGroupData
    },
    ...options,
    retry: (options.retry as number | boolean | undefined) ?? false,
  })

  return { ...query }
}

export const useMarkMessageGroupAsRead = () => {
  const api = useMangoApi()
  return useMutation({
    mutationFn: async (groupId: number) => {
      const { ok, data } = await api.markMessageGroupAsRead(groupId)
      if (!ok) return Promise.reject(data as ApiError)
      return data as { message: string }
    },
  })
}

interface MarkMessageGroupsAsReadParams {
  onSuccess?: (data: MarkMessageGroupsAsReadResult[]) => void
}

interface MarkMessageGroupsAsReadResult {
  groupId: number
  result: { message: string } | ApiError
}

export const useMarkMessageGroupsAsRead = ({
  onSuccess,
}: MarkMessageGroupsAsReadParams = {}) => {
  const api = useMangoApi()
  return useMutation({
    mutationFn: async (groupIds: number[]) => {
      if (groupIds.length === 0) {
        return []
      }

      const promises = groupIds.map(async (groupId) => {
        const returnValue: MarkMessageGroupsAsReadResult = {
          groupId,
          result: {},
        }
        const { ok, data } = await api.markMessageGroupAsRead(groupId)
        returnValue.result = ok
          ? (data as { message: string })
          : (data as ApiError)

        return returnValue
      })

      const returnValues = await Promise.all(promises)
      return returnValues
    },
    onSuccess,
  })
}

interface MarkMessageThreadAsReadParams {
  onSuccess?: ({
    data,
    threadId,
  }: {
    data: { message: string }
    threadId: number
  }) => void
}
export const useMarkMessageThreadAsRead = ({
  onSuccess,
}: MarkMessageThreadAsReadParams = {}) => {
  const api = useMangoApi()
  return useMutation({
    mutationFn: async (threadId: number) => {
      const { ok, data } = await api.markMessageThreadAsRead(threadId)
      if (!ok) return Promise.reject(data as ApiError)
      return data as { message: string }
    },
    onSuccess: (data, variables) => {
      onSuccess?.({
        data: data as { message: string },
        threadId: variables,
      })
    },
  })
}

interface UseSendMessageParams {
  onSuccess?: ({
    response,
    postMessageParams,
  }: {
    response: SendMessageResponse
    postMessageParams: PostMessageParams
  }) => void
  onError?: (error: unknown, postMessageParams: PostMessageParams) => unknown
}

export const useSendMessage = ({
  onSuccess,
  onError,
}: UseSendMessageParams = {}) => {
  const api = useMangoApi()
  return useMutation({
    mutationFn: async (params: PostMessageParams) => {
      const { ok, data } = await api.postMessage(params)
      if (!ok) return Promise.reject(data)
      return data
    },
    onSuccess: (data, variables) => {
      onSuccess?.({
        response: data!,
        postMessageParams: variables,
      })
    },
    onError: (error, postMessageParams) => {
      onError?.(error, postMessageParams)
    },
  })
}

interface CreateThreadResult {
  threadResult: CreateThreadResponse
  messageResult?: {
    ok: boolean
    message: Message
  }
}

interface CreateThreadParams {
  groupId: number
  externalPhoneNumber: string
  name: string
  message?: {
    body: string
    file?: File
  }
}

interface UseTryCreateMessageThreadParams {
  onSuccess?: (data: CreateThreadResult) => void
  onError?: (error: ApiError) => void
}

/** Attempts to retrieve an existing thread, if the thread doesn't exist, creates one.
 * Optionally sends a message if the @param message parameter is supplied.
 * If a message is sent, the onSuccess callback will be called with the thread and message results.
 */
export const useCreateThread = ({
  onSuccess,
  onError,
}: UseTryCreateMessageThreadParams = {}) => {
  const api = useMangoApi()
  return useMutation<CreateThreadResult, ApiError, CreateThreadParams>({
    onSuccess: (data) => {
      onSuccess?.(data)
    },
    onError: (error) => {
      onError?.(error)
    },
    mutationFn: async (params: CreateThreadParams) => {
      const { ok: threadCreationSuccessful, data: threadData } =
        await api.createThread({
          groupId: params.groupId,
          externalPhoneNumber: params.externalPhoneNumber,
          name: params.name,
        })
      if (!threadCreationSuccessful) return Promise.reject(threadData)

      const threadResult = threadData
      if (!params.message) {
        const result = {
          threadResult: threadResult!,
        }
        onSuccess?.(result)
        return result
      }

      const { ok: messageCreationSuccessful, data: messageCreationData } =
        await api.postMessage({
          threadId: threadData!.id,
          body: params.message.body,
          tempUuid: newUuid(),
          file: params.message.file,
        })

      if (!messageCreationSuccessful) {
        //if message creation fails, check to see if the thread is empty, if so, delete the thread
        const { ok: emptyThreadCheckResult, data: getMessageData } =
          await api.getMessages({
            threadId: threadData!.id,
            offset: 0,
            limit: 1,
          })

        if (!emptyThreadCheckResult) {
          //if we can't check if the thread is empty, log the error and reject, we can only delete the thread if we know it's empty
          logger.warn('Failed to check if thread is empty', {
            threadId: threadData!.id,
            error: getMessageData,
          })
          return Promise.reject(messageCreationData)
        }

        //if the thread is empty, delete it
        if (getMessageData!.count === 0) {
          logger.error('Message creation failed, deleting thread', null, {
            threadId: threadData!.id,
            messageData: params.message,
          })

          const { ok: threadDeletionSuccessful } = await api.deleteThread(
            threadData!.id,
          )

          if (!threadDeletionSuccessful) {
            logger.error(
              `Failed to delete thread after message creation failed. ThreadId:${
                threadData!.id
              }`,
            )
          }

          return Promise.reject(messageCreationData)
        }
      }

      const result = {
        threadResult: threadResult!,
        messageResult: {
          ok: messageCreationSuccessful,
          message: messageCreationData as Message,
        },
      }
      return result
    },
  })
}

interface UseUpdateThreadNameParams {
  onSuccess?: ({
    response,
    variables,
  }: {
    response: MessageThread
    variables: { threadId: number; name: string }
  }) => void
}
export const useUpdateThreadName = ({
  onSuccess,
}: UseUpdateThreadNameParams = {}) => {
  const api = useMangoApi()
  return useMutation({
    mutationFn: async (params: { threadId: number; name: string }) => {
      const { ok, data } = await api.updateThreadName(params)
      if (!ok) {
        return Promise.reject(data)
      }
      return data
    },
    onSuccess: (data, variables) => {
      onSuccess?.({
        response: data!,
        variables,
      })
    },
  })
}
